import Web3Utils, { isAddress } from 'web3-utils'
import Web3 from 'web3'

import { fetcher } from '@/shared/fetcher'
import { logError } from '@/shared/logger'
import { BytesSignatureResponse } from '@/shared/models/BytesSignatureResponse'
import { FUNCTION_DICTIONARY_URL } from '@/shared/config'

const SIGNATURE_CACHE = 'signature-cache'

/**
 * Extract string signature into method and parameters.
 * ie. setDataBatch(bytes32[],bytes[]) => { method: 'setDataBatch', params: ['bytes32[]', 'bytes[]'] }
 *
 * @param signature function signature that should be extracted
 * @returns extracted method and params
 */
export const extractSignature = (
  signature: string
): { method: string; params: string[] } => {
  const [method, paramsStr] = signature.slice(0, -1).split('(')
  const params = paramsStr.split(',')

  return { method, params }
}

/**
 * Checks if a given hex value is zero.
 * ie. 0x0 => 0; 0x0000 => 0
 *
 * @param hexValue hex value to be checked
 * @returns boolean => whether hexValue is zero or not
 */
export const isHexZero = (hexValue: string): boolean => {
  hexValue = hexValue.replace(/^0x/, '')
  const decimalValue = Number.parseInt(hexValue || '0', 16)
  return decimalValue === 0
}

/**
 * Gets a function signature. Returns an empty string if hexSignature
 * is not provided or an error is thrown.
 * If hexSignature is provided, it checks the cache storage.
 * If the signature is not found, it fetches 4bytes dictionary and stores
 * the result in the cache storage.
 *
 * @param string hexSignature - first 4 bytes of a payload
 * @param boolean preferError - if true, prefer error signatures
 * @returns function signature as a string
 */
export const getFunctionSignature = async (
  eth: Web3['eth'],
  input?: string,
  preferError = false
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<string> => {
  const data =
    input && !isHexZero(input)
      ? input.startsWith('0x')
        ? input.slice(2)
        : input
      : ''
  const selector = data.slice(0, 8)
  if (selector) {
    let signatureCache
    // TODO: refactor this to use the cache system of the tanstack query
    try {
      signatureCache = await globalThis?.window?.caches?.open(SIGNATURE_CACHE)
    } catch {}
    const signature = Web3Utils.stripHexPrefix(selector)
    const url = `${FUNCTION_DICTIONARY_URL}/${signature}`
    try {
      const functionSignatureResponse = await signatureCache?.match(url)
      if (functionSignatureResponse) {
        const entry = await functionSignatureResponse.json()
        return entry.functionSignature
      }

      const methods = await fetcher<BytesSignatureResponse, void>({
        method: 'GET',
        url,
      })

      if (methods && methods.results.length > 0) {
        let search = methods.results.reverse()
        if (preferError) {
          // In case we're preferring Errors,
          // then look for all methods containing Error
          // and put them first.
          const withError = search.filter(result =>
            /Error/.test(result.text_signature)
          )
          const withoutError = search.filter(
            result => !/Error/.test(result.text_signature)
          )
          search = [...withError, ...withoutError]
        }
        for (const result of search) {
          try {
            const params: string[] = result.text_signature
              .replaceAll(/^[^(]*\(|\)[^)]*$/g, '')
              .split(',')

            // Decode arguments according to function spec
            const args = eth.abi.decodeParameters(params, data.slice(8))

            // Build arg list (if there are bool values which are no 0 or 1 we can assume it's invalid)
            // Other values are validated in encodeParameters.
            const encodeArgs = Array.from({ length: params.length }).map(
              (_val, index) => {
                if (params[index] === 'bool') {
                  const val = args[`${index}`]
                  if (val != 1 && val != 0) {
                    // Due to javascript truthyness any non-empty or non-zero value is encoded as true without a problem.
                    // The output packet won't match but there is really no reason to even try.
                    throw new Error('Invalid boolean value')
                  }
                }
                if (params[index] === 'address') {
                  const val = args[`${index}`]
                  if (!isAddress(val)) {
                    throw new Error('Invalid address value' + val)
                  }
                }
                return args[`${index}`] ?? '0x'
              }
            )

            // Re-encode according to function spec.
            const newData = eth.abi
              .encodeParameters(params, encodeArgs)
              .slice(2)

            if (data.slice(8) === newData) {
              const item = {
                functionSignature: result.text_signature,
              }
              await signatureCache?.put(url, new Response(JSON.stringify(item)))
              return result.text_signature
            }
          } catch {
            // Ignore to try next record
          }
        }
      }
    } catch (error) {
      // This could be some kind of network error, so just make sure it gets into sentry
      logError(`Error while getting function signature:`, error)
    }
  }
  return ''
}
